import { BodyM, Heading2, TextLinkButton } from '@casavo/base-ui'
import cn from 'classnames'
import useTranslation from 'next-translate/useTranslation'
import { useEffect } from 'react'

import { CardPriorityWrapper } from '@/components/CardPriority/CardPriorityWrapper'
import { ConceptImage } from '@/components/ConceptImage'
import { ItemsCarousel } from '@/components/ItemsCarousel'
import { layoutContentInnerStretched } from '@/components/Layout/index.css'
import { ModalBase } from '@/components/Modal/ModalBase'
import { useBottomSheet } from '@/context/BottomSheetContext'
import { useDevice } from '@/context/Device'
import { useModal } from '@/context/ModalContext'
import { sprinkles } from '@/design-lib/style-theme/sprinkles.css'
import { theme } from '@/design-lib/theme'
import { SuggestedPotentialProperties } from '@/page-commons/listingsBySuggestedCriteria'
import { mapPrioritiesIntoCardDataPriority } from '@/utils/property-card'
import { GTMEventCategory } from '@/utils/tracking/eventCategory'
import { handleClick, handleShow } from '@/utils/tracking/eventsHandlers'

import { bannerPriorityNoResults, priorityCarousel } from '../styles.css'
import { SuggestedPriorityHelp } from '../SuggestedPriorityHelp'

type Props = {
  isLoading: boolean
  properties: SuggestedPotentialProperties
}

export const PotentialPropertiesList: React.FC<Props> = ({ isLoading, properties }) => {
  const { t } = useTranslation()
  const { openBottomSheet } = useBottomSheet()
  const device = useDevice()
  const { openModal } = useModal()
  const showNoResults = properties?.length === 0 && !isLoading
  const parsedPrioritiesIntoCardData = mapPrioritiesIntoCardDataPriority(properties)

  const openSuggestedPriorityModal = () => {
    handleClick(GTMEventCategory.SuggestedPage, 'WhatsPriority')
    if (device === 'mobile') {
      openBottomSheet(
        <div className={sprinkles({ paddingTop: 'l' })}>
          <SuggestedPriorityHelp />
        </div>,
        { bgColor: 'offWhite' }
      )
    } else {
      openModal(
        <ModalBase>
          <SuggestedPriorityHelp />
        </ModalBase>,
        { background: theme.colors.offWhite, overlay: true }
      )
    }
  }

  useEffect(() => {
    showNoResults && handleShow(GTMEventCategory.SuggestedPage, 'NoResults-PotentialProperties')
  }, [isLoading, properties])

  return (
    <>
      <div className={sprinkles({ marginBottom: '2xl' })}>
        <div
          className={sprinkles({
            alignItems: { desktop: 'flex-end' },
            display: { desktop: 'flex' },
            gap: { desktop: 'm' },
            marginBottom: { desktop: 'm', mobile: 'l' },
          })}
        >
          <Heading2
            className={sprinkles({
              marginBottom: { desktop: 0, mobile: 's' },
            })}
          >
            {t('suggested:Suggested_Priority_Title')}
          </Heading2>
          <TextLinkButton
            label={t('suggested:Suggested_Priority_HelpText')}
            type="button"
            onClick={openSuggestedPriorityModal}
          />
        </div>

        {showNoResults && (
          <div className={bannerPriorityNoResults} data-testid="banner-priorities-no-results">
            <ConceptImage height={59} name={'no-result'} width={59} />
            <BodyM>{t('suggested:Suggested_Priority_NoResult_Banner')}</BodyM>
          </div>
        )}

        {properties?.length > 0 && (
          <div className={cn(priorityCarousel, layoutContentInnerStretched)} data-testid="priorities-results-carousel">
            <ItemsCarousel itemsGap={device === 'mobile' ? 'm' : '3xl'} itemsWidth={328}>
              {parsedPrioritiesIntoCardData.map((priority) => {
                return (
                  <CardPriorityWrapper
                    key={priority.id}
                    cardData={priority}
                    data-testid="priority-card"
                    handleCardClick={() => handleClick(GTMEventCategory.SuggestedPage, 'ListingCard-PotentialProperty')}
                  />
                )
              })}
            </ItemsCarousel>
          </div>
        )}
      </div>
    </>
  )
}
